'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { RefereeCard } from '@/components/portfolio/referrals/referee-card';
import { RefereeRowCompact } from '@/components/portfolio/referrals/referee-row-compact';
import { useGetReferralReferees } from '@/components/portfolio/referrals/stub-hooks';
import type { Referee } from '@/components/portfolio/referrals/types';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { UserMultipleIcon } from '@/components/ui/icon/glyphs';
import { InfiniteList } from '@/components/ui/infinite-list';
import { scopeFields } from '@/lib/validation/scope-fields';
export type RefereeRowVariant = 'default' | 'compact';
interface RefereeListProps {
  userId: string;
  /** Row chrome — `'default'` renders the legacy <RefereeCard>; `'compact'`
   *  renders the spec-085 two-line right-aligned <RefereeRowCompact> used
   *  inside <ShowRefereesSheet>. */
  rowVariant?: RefereeRowVariant;
  /** When true, hides the section header (icon + label + count) — the parent
   *  surface (e.g. sheet) already supplies a title. */
  hideHeader?: boolean;
  /** SCAFFOLD: when provided, skips the SDK fetch and renders this list
   *  directly. Used by `<ShowRefereesSheet>` while the referrals backend is
   *  still being wired (see `_fake-data.ts`). Remove with `_fake-data.ts`. */
  injectedReferees?: ReadonlyArray<Referee>;
}
interface RefereeResponse {
  refereeUserId: string;
  displayName: string | null;
  handle: string | null;
  avatarUrl: string | null;
  avatarNsfw: boolean;
  avatarPreferences?: {
    rasterise: boolean;
  } | null;
  linkedAt: string;
  hasDownstream: boolean;
  totalTrades: number;
  totalVolume: string;
  earningsGenerated: string;
}
const PAGE_SIZE = 5;
export function RefereeList(t0) {
  const $ = _c(39);
  const {
    userId,
    rowVariant: t1,
    hideHeader: t2,
    injectedReferees
  } = t0;
  const rowVariant = t1 === undefined ? "default" : t1;
  const hideHeader = t2 === undefined ? false : t2;
  const t = useTranslations("social.referrals");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      fields: ["refereeUserId", "displayName", "handle", "avatarUrl", "avatarNsfw", "avatarPreferences", "linkedAt", "hasDownstream", "totalTrades", "totalVolume", "earningsGenerated"]
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = userId.length > 0 && injectedReferees === undefined;
  let t5;
  if ($[1] !== t4) {
    t5 = {
      enabled: t4
    };
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const {
    data,
    status
  } = useGetReferralReferees(userId, t3, t5);
  let fetchNextPage;
  let hasNextPage;
  let referees;
  let t6;
  if ($[3] !== data || $[4] !== injectedReferees || $[5] !== visibleCount) {
    const sdkReferees = scopeFields(Array.isArray(data) ? data as ReadonlyArray<Record<string, unknown>> : []).map(_temp);
    referees = injectedReferees !== undefined ? [...injectedReferees] : sdkReferees;
    hasNextPage = visibleCount < referees.length;
    fetchNextPage = function fetchNextPage() {
      setIsFetchingMore(true);
      setTimeout(() => {
        setVisibleCount(prev => Math.min(prev + PAGE_SIZE, referees.length));
        setIsFetchingMore(false);
      }, 300);
    };
    t6 = referees.slice(0, visibleCount);
    $[3] = data;
    $[4] = injectedReferees;
    $[5] = visibleCount;
    $[6] = fetchNextPage;
    $[7] = hasNextPage;
    $[8] = referees;
    $[9] = t6;
  } else {
    fetchNextPage = $[6];
    hasNextPage = $[7];
    referees = $[8];
    t6 = $[9];
  }
  const visibleReferees = t6;
  const isInitialLoading = status === "loading" && injectedReferees === undefined;
  let t7;
  if ($[10] !== hideHeader || $[11] !== referees || $[12] !== t) {
    t7 = hideHeader ? null : <div className="flex items-center gap-2"><Icon icon={UserMultipleIcon} className="size-4 text-foreground-secondary" /><h3 className="text-body font-medium">{t("people_referred")}</h3><span className="text-caption tabular-nums text-foreground-secondary">({String(referees.length)})</span></div>;
    $[10] = hideHeader;
    $[11] = referees;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== rowVariant) {
    t8 = referee_0 => rowVariant === "compact" ? <RefereeRowCompact referee={referee_0} /> : <RefereeCard referee={referee_0} />;
    $[14] = rowVariant;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex flex-col gap-3">{Array.from({
        length: 3
      }).map(_temp3)}</div>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("no_referees");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <p className="text-body font-medium">{t10}</p>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("no_referees_description");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12) {
    t13 = <p className="text-caption text-foreground-secondary">{t12}</p>;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t11 || $[26] !== t13) {
    t14 = <div className="flex flex-col items-center gap-2 py-8 text-center">{t11}{t13}</div>;
    $[25] = t11;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== fetchNextPage || $[29] !== hasNextPage || $[30] !== isFetchingMore || $[31] !== isInitialLoading || $[32] !== t14 || $[33] !== t8 || $[34] !== visibleReferees) {
    t15 = <InfiniteList items={visibleReferees} getItemKey={_temp2} renderItem={t8} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingMore} fetchNextPage={fetchNextPage} isInitialLoading={isInitialLoading} loadingPlaceholder={t9} emptyState={t14} listClassName="flex flex-col gap-3" maxHeight={520} scrollMode="page-below-lg" />;
    $[28] = fetchNextPage;
    $[29] = hasNextPage;
    $[30] = isFetchingMore;
    $[31] = isInitialLoading;
    $[32] = t14;
    $[33] = t8;
    $[34] = visibleReferees;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== t7) {
    t16 = <div className="flex flex-col gap-4">{t7}{t15}</div>;
    $[36] = t15;
    $[37] = t7;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
}
function _temp3(_, i) {
  return <Skeleton key={`referee-loading-${String(i)}`} className="h-20 w-full rounded-card" />;
}
function _temp2(referee) {
  return referee.refereeUserId;
}
function _temp(r) {
  return {
    refereeUserId: r.refereeUserId,
    displayName: r.displayName,
    handle: r.handle,
    avatarUrl: r.avatarUrl,
    avatarNsfw: r.avatarNsfw,
    avatarPreferences: r.avatarPreferences ?? null,
    linkedAt: r.linkedAt,
    hasDownstream: r.hasDownstream,
    totalTrades: r.totalTrades,
    totalVolume: r.totalVolume,
    earningsGenerated: r.earningsGenerated
  };
}